<template>
  <div v-if="visible" class="fixed inset-0 z-50 overflow-y-auto">
    <div
      class="fixed inset-0 bg-black/50 transition-opacity duration-300"
      @click="closeModal"
    ></div>
    <div class="relative min-h-screen flex items-center justify-center p-4">
      <div
        class="relative bg-white rounded-2xl shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden transform transition-all duration-300 scale-100 flex flex-col"
      >
        <!-- 弹窗标题 -->
        <div class="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h3 class="text-lg font-semibold text-slate-900">{{ isEdit ? '编辑方案' : '新建方案' }}</h3>
            <p class="text-sm text-slate-500 mt-0.5">请按照以下步骤完成方案信息填写</p>
          </div>
          <button @click="closeModal" class="p-1.5 text-slate-400 hover:text-slate-600">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <!-- 步骤指示器 -->
        <div class="flex-shrink-0 px-6 py-4 bg-slate-50 border-b border-slate-200">
          <div class="flex items-center justify-between max-w-4xl mx-auto">
            <template v-for="(step, index) in wizardSteps" :key="step.value">
              <div class="flex items-center">
                <div class="flex items-center">
                  <div :class="[
                    'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-300',
                    currentStep > step.value ? 'bg-green-500 text-white' :
                    currentStep === step.value ? 'bg-primary-600 text-white' :
                    'bg-slate-200 text-slate-500'
                  ]">
                    <svg v-if="currentStep > step.value" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                    </svg>
                    <span v-else>{{ index + 1 }}</span>
                  </div>
                  <span :class="[
                    'ml-2 text-sm font-medium transition-colors whitespace-nowrap',
                    currentStep >= step.value ? 'text-slate-900' : 'text-slate-400'
                  ]">{{ step.label }}</span>
                </div>
                <div v-if="index < wizardSteps.length - 1" :class="[
                  'flex-1 h-0.5 mx-4 transition-colors duration-300',
                  currentStep > step.value ? 'bg-green-500' : 'bg-slate-200'
                ]"></div>
              </div>
            </template>
          </div>
        </div>

        <!-- 弹窗内容区域 -->
        <div class="flex-1 overflow-y-auto min-h-0">
          <!-- 步骤一：基本信息 -->
          <div v-show="currentStep === 1" class="p-6">
            <div class="max-w-2xl mx-auto">
              <div class="mb-6">
                <h4 class="text-base font-medium text-slate-900 mb-1">方案基本信息</h4>
                <p class="text-sm text-slate-500">填写方案的名称、客户以及详细说明信息</p>
              </div>

              <div class="space-y-5">
                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-2">
                    方案名称 <span class="text-red-500">*</span>
                  </label>
                  <div class="flex flex-wrap gap-2 mb-2">
                    <label v-for="tag in planForm.nameOptions" :key="tag" class="flex items-center gap-1.5 px-3 py-1.5 border rounded-lg cursor-pointer transition-all duration-200"
                      :class="planForm.nameTags.includes(tag) ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-slate-300 hover:border-primary-300 hover:bg-primary-50'">
                      <input
                        type="checkbox"
                        :value="tag"
                        v-model="planForm.nameTags"
                        class="sr-only"
                      >
                      <span class="text-sm">{{ tag }}</span>
                      <svg v-if="planForm.nameTags.includes(tag)" class="w-4 h-4 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                      </svg>
                    </label>
                  </div>
                  <input
                    :value="planForm.name"
                    type="text"
                    readonly
                    class="form-input form-readonly w-full"
                    placeholder="请勾选上方选项"
                  >
                </div>

                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-2">
                    客户 <span class="text-red-500">*</span>
                  </label>
                  <el-select
                    v-model="planForm.customerId"
                    placeholder="请选择客户"
                    filterable
                    remote
                    :remote-method="handleCustomerSearch"
                    :loading="customerLoading"
                    clearable
                    class="w-full form-select"
                  >
                    <el-option
                      v-for="c in customerList"
                      :key="c.id"
                      :label="c.name"
                      :value="c.id"
                    />
                  </el-select>
                </div>

                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-2">方案详情</label>
                  <RichTextEditor v-model="planForm.detailContent" upload-module="plan" class="mt-2" />
                  <p class="mt-2 text-xs text-slate-500">支持富文本格式，保存后将作为方案详情展示。</p>
                </div>

                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-2">备注</label>
                  <textarea
                    v-model="planForm.remark"
                    rows="3"
                    class="form-textarea"
                    placeholder="请输入备注信息（可选）"
                  ></textarea>
                </div>
              </div>
            </div>
          </div>

          <!-- 步骤二：订单与账单 -->
          <div v-show="currentStep === 2" class="p-6">
            <div class="max-w-4xl mx-auto">
              <div class="mb-6">
                <h4 class="text-base font-medium text-slate-900 mb-1">订单与账单信息</h4>
                <p class="text-sm text-slate-500">添加订单信息，包括产品、类型、执行人员以及账单明细</p>
              </div>

              <!-- 订单列表 -->
              <div class="space-y-4">
                <div v-for="(order, orderIndex) in planForm.orders" :key="orderIndex" class="border border-slate-200 rounded-xl overflow-hidden">
                  <!-- 订单头部 -->
                  <div class="bg-slate-50 px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                    <div class="flex items-center gap-3">
                      <div class="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
                        <svg class="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
                        </svg>
                      </div>
                      <span class="font-medium text-slate-900">订单 {{ orderIndex + 1 }}</span>
                      <span :class="[
                        'px-2 py-0.5 rounded text-xs font-medium',
                        order.orderType === 'ONCE' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                      ]">
                        {{ order.orderType === 'ONCE' ? '一次性服务' : '分期服务' }}
                      </span>
                    </div>
                    <button @click="removeOrder(orderIndex)" class="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                      </svg>
                    </button>
                  </div>

                  <!-- 订单内容 -->
                  <div class="p-4">
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <ProductPicker
                        v-model="order.productId"
                        :categories="categories"
                        @change="(product) => handleProductChange(order, product)"
                      />
                      <div>
                        <label class="block text-sm font-medium text-slate-700 mb-1">
                          订单类型 <span class="text-red-500">*</span>
                        </label>
                        <el-select
                          v-model="order.orderType"
                          placeholder="请选择类型"
                          class="w-full form-select"
                          @change="handleOrderTypeChange(order)"
                        >
                          <el-option label="一次性服务" value="ONCE" />
                          <el-option label="分期服务" value="PERIODIC" />
                        </el-select>
                      </div>
                      <div>
                        <label class="block text-sm font-medium text-slate-700 mb-1">
                          落地执行人员 <span class="text-red-500">*</span>
                        </label>
                        <el-select
                          v-model="order.executorId"
                          placeholder="请选择执行人员"
                          filterable
                          remote
                          reserve-keyword
                          :remote-method="(val) => handleExecutorSearch(val, order)"
                          :loading="order.executorLoading"
                          class="w-full form-select"
                          @visible-change="(visible) => handleExecutorDropdownVisible(visible, order)"
                        >
                          <el-option
                            v-for="u in order.executorList || []"
                            :key="u.id"
                            :label="u.displayName || u.username"
                            :value="u.id"
                          >
                            <div class="flex items-center justify-between">
                              <span>{{ u.displayName || u.username }}</span>
                              <span v-if="u.phone" class="ml-2 text-xs text-slate-400">{{ u.phone }}</span>
                            </div>
                          </el-option>
                          <template v-if="order.executorPagination?.total > (order.executorList || []).length" #footer>
                            <div class="border-t border-slate-200 py-2 text-center">
                              <button
                                type="button"
                                class="text-sm text-primary-600 hover:text-primary-700 disabled:opacity-50"
                                :disabled="order.executorLoading"
                                @click.stop="loadMoreExecutors(order)"
                              >
                                {{ order.executorLoading ? '加载中...' : `加载更多 (${(order.executorList || []).length}/${order.executorPagination.total})` }}
                              </button>
                            </div>
                          </template>
                        </el-select>
                      </div>
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label class="block text-sm font-medium text-slate-700 mb-1">服务开始时间</label>
                        <el-date-picker
                          v-model="order.serviceStart"
                          type="date"
                          format="YYYY年MM月DD日"
                          value-format="YYYY-MM-DD"
                          placeholder="请选择开始时间"
                          class="w-full form-date"
                          @change="updatePeriodCountByServiceRange(order)"
                        />
                      </div>
                      <div>
                        <label class="block text-sm font-medium text-slate-700 mb-1">服务结束时间</label>
                        <el-date-picker
                          v-model="order.serviceEnd"
                          type="date"
                          format="YYYY年MM月DD日"
                          value-format="YYYY-MM-DD"
                          placeholder="请选择结束时间"
                          class="w-full form-date"
                          @change="updatePeriodCountByServiceRange(order)"
                        />
                      </div>
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label class="block text-sm font-medium text-slate-700 mb-1">
                          订单金额 <span class="text-red-500">*</span>
                        </label>
                        <div class="relative">
                          <span class="absolute left-3 top-2.5 text-slate-500">¥</span>
                          <input
                            v-model.number="order.amount"
                            type="number"
                            step="1"
                            class="form-input form-input-with-prefix w-full"
                            placeholder="0.00"
                          >
                        </div>
                      </div>
                      <div v-if="order.orderType === 'PERIODIC'">
                        <label class="block text-sm font-medium text-slate-700 mb-1">
                          分期期数 <span class="text-red-500">*</span>
                        </label>
                        <input
                          v-model.number="order.periodCount"
                          type="number"
                          min="1"
                          step="1"
                          class="form-input w-full"
                          placeholder="请输入期数"
                          @input="updateServiceEndByPeriodCount(order)"
                        >
                      </div>
                    </div>

                    <div class="mb-4">
                      <label class="block text-sm font-medium text-slate-700 mb-1">备注</label>
                      <input
                        v-model="order.remark"
                        type="text"
                        class="form-input w-full"
                        placeholder="请输入备注（可选）"
                      >
                    </div>

                    <!-- 账单信息 -->
                    <div class="border-t border-slate-200 pt-4">
                      <div class="flex items-center justify-between mb-3">
                        <div class="flex items-center gap-2">
                          <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>
                          </svg>
                          <span class="text-sm font-medium text-slate-700">账单明细</span>
                        </div>
                        <div class="flex gap-2">
                          <button @click="addBilling(order)" class="text-slate-600 text-sm hover:text-slate-800 font-medium px-2 py-1 hover:bg-slate-100 rounded">
                            + 手动添加
                          </button>
                          <button @click="generateBillings(order)" class="text-primary-600 text-sm hover:text-primary-700 font-medium px-2 py-1 hover:bg-primary-50 rounded">
                            <svg class="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                            </svg>
                            自动生成
                          </button>
                        </div>
                      </div>

                      <div v-if="order.billings && order.billings.length > 0" class="space-y-2">
                        <!-- 表头 -->
                        <div class="flex items-center gap-3 px-3 pb-2 text-xs text-slate-500">
                          <span class="w-16">期数</span>
                          <span class="flex-[1.4] text-left pl-6">金额</span>
                          <span class="w-24">付款日期</span>
                          <span class="w-8"></span>
                        </div>
                        <!-- 账单行 -->
                        <div v-for="(billing, billingIndex) in order.billings" :key="billingIndex" class="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                          <input
                            v-model.number="billing.periodIndex"
                            type="number"
                            placeholder="期次"
                            class="form-input form-control-sm w-16 text-center"
                          >
                          <div class="relative flex-[1.4] min-w-[180px]">
                            <span class="absolute left-2 top-1.5 text-slate-500 text-sm">¥</span>
                            <input
                              v-model.number="billing.amount"
                              type="number"
                              step="0.01"
                              class="form-input form-control-sm form-input-with-prefix w-full"
                              placeholder="0.00"
                              @input="syncOrderAmountFromBillings(order)"
                            >
                          </div>
                          <el-date-picker
                            v-model="billing.dueDate"
                            type="date"
                            format="YYYY年MM月DD日"
                            value-format="YYYY-MM-DD"
                            placeholder="到期日"
                            class="w-28 form-date form-control-sm"
                          />
                          <button @click="removeBilling(order, billingIndex)" class="p-1 text-red-500 hover:text-red-700">
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                            </svg>
                          </button>
                        </div>
                      </div>
                      <div v-else class="text-center py-4 text-slate-500 text-sm bg-slate-50 rounded-lg">
                        暂无账单，可手动添加或自动生成
                      </div>

                      <div class="mt-3 flex items-center justify-end gap-4">
                        <span class="text-sm text-slate-600">账单金额合计: </span>
                        <span :class="['font-medium', isBillingTotalValid(order) ? 'text-green-600' : 'text-red-600']">
                          ¥{{ getBillingTotal(order).toLocaleString() }}
                        </span>
                        <span v-if="!isBillingTotalValid(order)" class="text-xs text-red-600">（与订单金额不一致）</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 添加订单按钮 -->
                <button @click="addOrder" class="w-full py-4 border-2 border-dashed border-slate-300 rounded-xl text-slate-500 hover:border-primary-400 hover:text-primary-600 hover:bg-primary-50 transition-colors flex items-center justify-center gap-2">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                  </svg>
                  添加订单
                </button>

                <!-- 订单汇总 -->
                <div class="bg-slate-50 rounded-xl p-4 flex items-center justify-between">
                  <span class="text-sm text-slate-600">共计 <span class="font-medium text-slate-900">{{ planForm.orders.length }}</span> 个订单</span>
                  <div class="flex items-center gap-2">
                    <span class="text-sm text-slate-600">方案总金额</span>
                    <span class="text-xl font-bold text-primary-600">¥{{ orderTotalAmount.toLocaleString() }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 步骤三：提醒设置 -->
          <div v-show="currentStep === 3" class="p-6">
            <div class="max-w-4xl mx-auto">
              <!-- 方案跟进提醒 -->
              <div class="mb-8">
                <div class="mb-4">
                  <h4 class="text-base font-medium text-slate-900 mb-1">方案跟进提醒</h4>
                  <p class="text-sm text-slate-500">设置方案执行过程中的跟进提醒，可添加多个提醒节点</p>
                </div>

                <!-- 提醒列表 -->
                <div class="space-y-4 mb-4">
                  <div v-for="(reminder, reminderIndex) in planForm.reminders" :key="reminder.id || reminder._localKey || reminderIndex" class="border border-slate-200 rounded-xl overflow-hidden">
                    <!-- 提醒头部 -->
                    <div class="bg-slate-50 px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                      <div class="flex items-center gap-3">
                        <div class="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                          <svg class="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/>
                          </svg>
                        </div>
                        <span class="font-medium text-slate-900">提醒 {{ reminderIndex + 1 }}</span>
                        <span v-if="reminder.enabled === false" class="px-2 py-0.5 bg-slate-200 text-slate-500 rounded text-xs font-medium">已关闭</span>
                      </div>
                      <div class="flex items-center gap-2">
                        <div class="flex items-center gap-2">
                          <span class="text-sm text-slate-500">提醒</span>
                          <label class="form-switch">
                            <input
                              type="checkbox"
                              class="form-switch-input"
                              :checked="reminder.enabled !== false"
                              @change="toggleReminder(reminder)"
                            >
                            <span class="form-switch-track"></span>
                          </label>
                        </div>
                        <button @click="removeReminder(reminderIndex)" class="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors">
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                          </svg>
                        </button>
                      </div>
                    </div>

                    <!-- 提醒内容 -->
                    <div class="p-4" :class="reminder.enabled === false ? 'opacity-60 pointer-events-none' : ''">
                      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label class="block text-sm font-medium text-slate-700 mb-1">
                            首次提醒时间 <span class="text-red-500">*</span>
                          </label>
                          <el-date-picker
                            v-model="reminder.firstReminderTime"
                            type="datetime"
                            format="YYYY年MM月DD日 HH:mm"
                            value-format="YYYY-MM-DDTHH:mm:ss"
                            placeholder="请选择提醒时间"
                            class="w-full form-date"
                            :disabled="reminder.enabled === false"
                          />
                        </div>
                        <div>
                          <label class="block text-sm font-medium text-slate-700 mb-1">
                            提醒事项 <span class="text-red-500">*</span>
                          </label>
                          <input
                            v-model="reminder.reminderItem"
                            type="text"
                            class="form-input w-full"
                            placeholder="请输入提醒事项"
                            :disabled="reminder.enabled === false"
                          >
                        </div>
                      </div>

                      <!-- 连续提醒设置 -->
                      <div class="border-t border-slate-200 pt-4">
                        <div class="flex items-center gap-3 mb-3">
                          <label class="form-switch" :class="{ 'opacity-60': reminder.enabled === false }">
                            <input
                              type="checkbox"
                              class="form-switch-input"
                              :checked="reminder.repeatEnabled"
                              :disabled="reminder.enabled === false"
                              @change="reminder.repeatEnabled = !reminder.repeatEnabled"
                            >
                            <span class="form-switch-track"></span>
                          </label>
                          <span class="text-sm font-medium text-slate-700">开启连续提醒</span>
                        </div>

                        <div v-if="reminder.repeatEnabled" class="pl-12">
                          <div class="flex items-center gap-6">
                            <div class="flex items-center gap-3">
                              <span class="text-sm text-slate-600">提醒间隔</span>
                              <div class="flex items-center gap-2">
                                <input
                                  v-model.number="reminder.repeatIntervalValue"
                                  type="number"
                                  min="1"
                                  class="form-input form-control-sm w-20 text-center"
                                  placeholder="1"
                                  :disabled="reminder.enabled === false"
                                >
                                <el-select
                                  v-model="reminder.repeatIntervalUnit"
                                  class="w-24 form-select form-control-sm"
                                  :disabled="reminder.enabled === false"
                                >
                                  <el-option label="天" value="DAY" />
                                  <el-option label="小时" value="HOUR" />
                                  <el-option label="分钟" value="MINUTE" />
                                </el-select>
                              </div>
                            </div>
                            <div class="flex items-center gap-3">
                              <span class="text-sm text-slate-600">最大次数</span>
                              <input
                                v-model.number="reminder.maxReminderCount"
                                type="number"
                                min="1"
                                class="form-input form-control-sm w-20 text-center"
                                placeholder="10"
                                :disabled="reminder.enabled === false"
                              >
                              <span class="text-sm text-slate-500">次</span>
                            </div>
                          </div>
                          <p class="mt-2 text-xs text-slate-500">当到指定时间未处理时，将按设置的间隔持续提醒</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 添加提醒按钮 -->
                <button @click="addReminder" class="w-full py-4 border-2 border-dashed border-slate-300 rounded-xl text-slate-500 hover:border-primary-400 hover:text-primary-600 hover:bg-primary-50 transition-colors flex items-center justify-center gap-2">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                  </svg>
                  添加提醒
                </button>

                <!-- 空状态提示 -->
                <div v-if="planForm.reminders.length === 0" class="text-center py-8 text-slate-500">
                  <p class="text-sm">暂无方案跟进提醒</p>
                  <p class="text-xs mt-1">点击上方按钮添加跟进提醒</p>
                </div>
              </div>

              <!-- 方案账单提醒 -->
              <div class="mb-8">
                <div class="mb-4 flex items-center justify-between">
                  <div>
                    <h4 class="text-base font-medium text-slate-900 mb-1">方案账单提醒</h4>
                    <p class="text-sm text-slate-500">在账单应收日期前提醒，确保及时收款</p>
                  </div>
                  <!-- 已添加时显示开关和删除，未添加时显示添加按钮 -->
                  <div v-if="planForm.billingReminderSetting.id || planForm.billingReminderSetting._added" class="flex items-center gap-2">
                    <span class="text-sm text-slate-500">提醒</span>
                    <label class="form-switch">
                      <input
                        type="checkbox"
                        class="form-switch-input"
                        :checked="planForm.billingReminderSetting?.enabled"
                        @change="toggleBillingReminder"
                      >
                      <span class="form-switch-track"></span>
                    </label>
                    <button @click="removeBillingReminder" class="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                      </svg>
                    </button>
                  </div>
                  <button v-else @click="addBillingReminder" class="px-3 py-1.5 text-sm text-primary-600 hover:bg-primary-50 rounded-lg transition-colors">
                    添加账单提醒
                  </button>
                </div>

                <!-- 账单提醒表单 -->
                <div
                  v-if="planForm.billingReminderSetting.id || planForm.billingReminderSetting._added"
                  class="border border-slate-200 rounded-xl overflow-hidden"
                >
                  <div class="bg-slate-50 px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                    <div class="flex items-center gap-3">
                      <div class="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                        <svg class="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"/>
                        </svg>
                      </div>
                      <span class="font-medium text-slate-900">账单提醒</span>
                      <span v-if="!planForm.billingReminderSetting?.enabled" class="px-2 py-0.5 bg-slate-200 text-slate-500 rounded text-xs font-medium">已关闭</span>
                    </div>
                  </div>

                  <div class="p-4" :class="!planForm.billingReminderSetting?.enabled ? 'opacity-60 pointer-events-none' : ''">
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <label class="block text-sm font-medium text-slate-700 mb-1">提前天数</label>
                        <div class="flex items-center gap-2">
                          <input
                            v-model.number="planForm.billingReminderSetting.advanceDays"
                            type="number"
                            min="0"
                            class="form-input form-control-sm flex-1"
                            placeholder="0"
                            :disabled="!planForm.billingReminderSetting?.enabled"
                          >
                          <span class="text-sm text-slate-500">天</span>
                        </div>
                        <p class="mt-1 text-xs text-slate-500">0表示应收日当天</p>
                      </div>
                      <div>
                        <label class="block text-sm font-medium text-slate-700 mb-1">每日提醒时间</label>
                        <el-time-picker
                          v-model="planForm.billingReminderSetting.remindTime"
                          format="HH:mm"
                          value-format="HH:mm:ss"
                          placeholder="选择时间"
                          class="w-full form-date form-control-sm"
                          :disabled="!planForm.billingReminderSetting?.enabled"
                        />
                      </div>
                      
                    </div>

                    <!-- 连续提醒 -->
                    <div class="border-t border-slate-200 pt-4">
                      <div class="flex items-center gap-3 mb-3">
                        <label class="form-switch" :class="{ 'opacity-60': !planForm.billingReminderSetting?.enabled }">
                          <input
                            type="checkbox"
                            class="form-switch-input"
                            :checked="planForm.billingReminderSetting.repeatEnabled"
                            :disabled="!planForm.billingReminderSetting?.enabled"
                            @change="planForm.billingReminderSetting.repeatEnabled = !planForm.billingReminderSetting.repeatEnabled"
                          >
                          <span class="form-switch-track"></span>
                        </label>
                        <span class="text-sm font-medium text-slate-700">开启连续提醒</span>
                      </div>

                      <div v-if="planForm.billingReminderSetting.repeatEnabled" class="pl-12">
                        <div class="flex items-center gap-6">
                          <div class="flex items-center gap-3">
                            <span class="text-sm text-slate-600">提醒间隔</span>
                            <div class="flex items-center gap-2">
                              <input
                                v-model.number="planForm.billingReminderSetting.repeatIntervalValue"
                                type="number"
                                min="1"
                                class="form-input form-control-sm w-20 text-center"
                                placeholder="1"
                                :disabled="!planForm.billingReminderSetting?.enabled"
                              >
                              <el-select
                                v-model="planForm.billingReminderSetting.repeatIntervalUnit"
                                class="w-24 form-select form-control-sm"
                                :disabled="!planForm.billingReminderSetting?.enabled"
                              >
                                <el-option label="天" value="DAY" />
                                <el-option label="小时" value="HOUR" />
                                <el-option label="分钟" value="MINUTE" />
                              </el-select>
                            </div>
                          </div>
                          <div class="flex items-center gap-3">
                            <span class="text-sm text-slate-600">最大次数</span>
                            <input
                              v-model.number="planForm.billingReminderSetting.maxReminderCount"
                              type="number"
                              min="1"
                              class="form-input form-control-sm w-20 text-center"
                              placeholder="10"
                              :disabled="!planForm.billingReminderSetting?.enabled"
                            >
                            <span class="text-sm text-slate-500">次</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 账单提醒空状态 -->
                <div v-else class="text-center py-8 text-slate-500 border border-dashed border-slate-300 rounded-xl">
                  <p class="text-sm">暂无账单提醒</p>
                  <p class="text-xs mt-1">点击上方按钮添加账单提醒</p>
                </div>
              </div>

              <!-- 订单续约提醒 -->
              <div>
                <div class="mb-4">
                  <h4 class="text-base font-medium text-slate-900 mb-1">订单续约提醒</h4>
                  <p class="text-sm text-slate-500">在订单服务结束前提醒客户续约，一次性订单和分期订单都可设置</p>
                </div>

                <div v-if="planForm.orders.length === 0" class="text-center py-8 text-slate-500 border border-dashed border-slate-300 rounded-xl">
                  <p class="text-sm">暂无订单</p>
                  <p class="text-xs mt-1">请先在「订单与账单」中添加订单</p>
                </div>

                <div v-else class="space-y-4">
                  <div v-for="(order, orderIndex) in planForm.orders" :key="order.id || order._localKey || orderIndex" class="border border-slate-200 rounded-xl overflow-hidden">
                    <div class="bg-slate-50 px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                      <div class="flex items-center gap-3">
                        <div class="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                          <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                          </svg>
                        </div>
                        <span class="font-medium text-slate-900">{{ order.productNameSnapshot || '订单 ' + (orderIndex + 1) }}</span>
                        <span v-if="order.orderType === 'ONCE'" class="px-2 py-0.5 bg-slate-200 text-slate-500 rounded text-xs font-medium">一次性</span>
                        <span v-else class="px-2 py-0.5 bg-blue-100 text-blue-600 rounded text-xs font-medium">分期</span>
                      </div>
                      <!-- 已添加提醒时显示开关和删除 -->
                      <div v-if="order.renewalReminderSetting?.id || order.renewalReminderSetting?._added" class="flex items-center gap-2">
                        <span class="text-sm text-slate-500">提醒</span>
                        <label class="form-switch">
                          <input
                            type="checkbox"
                            class="form-switch-input"
                            :checked="order.renewalReminderSetting?.enabled"
                            @change="toggleOrderRenewalReminder(order)"
                          >
                          <span class="form-switch-track"></span>
                        </label>
                        <button @click="removeOrderRenewalReminder(order)" class="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors">
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                          </svg>
                        </button>
                      </div>
                      <!-- 未添加提醒时显示添加按钮 -->
                      <button v-else @click="addOrderRenewalReminder(order)" class="px-3 py-1.5 text-sm text-primary-600 hover:bg-primary-50 rounded-lg transition-colors">
                        添加续约提醒
                      </button>
                    </div>

                    <!-- 续约提醒表单 -->
                    <div v-if="order.renewalReminderSetting?.id || order.renewalReminderSetting?._added" class="p-4" :class="!order.renewalReminderSetting?.enabled ? 'opacity-60 pointer-events-none' : ''">
                      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label class="block text-sm font-medium text-slate-700 mb-1">提前天数</label>
                          <div class="flex items-center gap-2">
                            <input
                              v-model.number="order.renewalReminderSetting.advanceDays"
                              type="number"
                              min="0"
                              class="form-input form-control-sm flex-1"
                              placeholder="0"
                              :disabled="!order.renewalReminderSetting?.enabled"
                            >
                            <span class="text-sm text-slate-500">天</span>
                          </div>
                          <p class="mt-1 text-xs text-slate-500">0表示服务结束当天</p>
                        </div>
                        <div>
                          <label class="block text-sm font-medium text-slate-700 mb-1">每日提醒时间</label>
                          <el-time-picker
                            v-model="order.renewalReminderSetting.remindTime"
                            format="HH:mm"
                            value-format="HH:mm:ss"
                            placeholder="选择时间"
                            class="w-full form-date form-control-sm"
                            :disabled="!order.renewalReminderSetting?.enabled"
                          />
                        </div>
                        
                      </div>

                      <!-- 连续提醒 -->
                      <div class="border-t border-slate-200 pt-4">
                        <div class="flex items-center gap-3 mb-3">
                          <label class="form-switch" :class="{ 'opacity-60': !order.renewalReminderSetting?.enabled }">
                            <input
                              type="checkbox"
                              class="form-switch-input"
                              :checked="order.renewalReminderSetting.repeatEnabled"
                              :disabled="!order.renewalReminderSetting?.enabled"
                              @change="order.renewalReminderSetting.repeatEnabled = !order.renewalReminderSetting.repeatEnabled"
                            >
                            <span class="form-switch-track"></span>
                          </label>
                          <span class="text-sm font-medium text-slate-700">开启连续提醒</span>
                        </div>

                        <div v-if="order.renewalReminderSetting.repeatEnabled" class="pl-12">
                          <div class="flex items-center gap-6">
                            <div class="flex items-center gap-3">
                              <span class="text-sm text-slate-600">提醒间隔</span>
                              <div class="flex items-center gap-2">
                                <input
                                  v-model.number="order.renewalReminderSetting.repeatIntervalValue"
                                  type="number"
                                  min="1"
                                  class="form-input form-control-sm w-20 text-center"
                                  placeholder="1"
                                  :disabled="!order.renewalReminderSetting?.enabled"
                                >
                                <el-select
                                  v-model="order.renewalReminderSetting.repeatIntervalUnit"
                                  class="w-24 form-select form-control-sm"
                                  :disabled="!order.renewalReminderSetting?.enabled"
                                >
                                  <el-option label="天" value="DAY" />
                                  <el-option label="小时" value="HOUR" />
                                  <el-option label="分钟" value="MINUTE" />
                                </el-select>
                              </div>
                            </div>
                            <div class="flex items-center gap-3">
                              <span class="text-sm text-slate-600">最大次数</span>
                              <input
                                v-model.number="order.renewalReminderSetting.maxReminderCount"
                                type="number"
                                min="1"
                                class="form-input form-control-sm w-20 text-center"
                                placeholder="10"
                                :disabled="!order.renewalReminderSetting?.enabled"
                              >
                              <span class="text-sm text-slate-500">次</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 步骤四：确认信息 -->
          <div v-show="currentStep === 4" class="p-6">
            <div class="max-w-3xl mx-auto">
              <div class="mb-6">
                <h4 class="text-base font-medium text-slate-900 mb-1">确认方案信息</h4>
                <p class="text-sm text-slate-500">请仔细核对以下信息，确认无误后提交</p>
              </div>

              <!-- 方案基本信息 -->
              <div class="bg-white border border-slate-200 rounded-xl overflow-hidden mb-4">
                <div class="bg-slate-50 px-4 py-3 border-b border-slate-200 flex items-center gap-2">
                  <svg class="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                  </svg>
                  <span class="font-medium text-slate-900">方案信息</span>
                </div>
                <div class="p-4">
                  <div class="grid grid-cols-2 gap-4">
                    <div>
                      <div class="text-xs text-slate-500 mb-1">方案名称</div>
                      <div class="font-medium text-slate-900">{{ planForm.name || '-' }}</div>
                    </div>
                    <div>
                      <div class="text-xs text-slate-500 mb-1">客户</div>
                      <div class="font-medium text-slate-900">{{ getCustomerName(planForm.customerId) }}</div>
                    </div>
                    <div class="col-span-2">
                      <div class="text-xs text-slate-500 mb-1">方案详情</div>
                      <div class="text-sm text-slate-600" v-html="planForm.detailContent || '-'"></div>
                    </div>
                    <div class="col-span-2">
                      <div class="text-xs text-slate-500 mb-1">备注</div>
                      <div class="text-sm text-slate-600">{{ planForm.remark || '-' }}</div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 订单汇总 -->
              <div class="bg-white border border-slate-200 rounded-xl overflow-hidden mb-4">
                <div class="bg-slate-50 px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <svg class="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
                    </svg>
                    <span class="font-medium text-slate-900">订单明细</span>
                  </div>
                  <span class="text-sm text-slate-500">共 {{ planForm.orders.length }} 个订单</span>
                </div>
                <div class="divide-y divide-slate-200">
                  <div v-for="(order, index) in planForm.orders" :key="index" class="p-4">
                    <div class="flex items-start justify-between mb-3">
                      <div class="flex items-center gap-2">
                        <span class="font-medium text-slate-900">订单 {{ index + 1 }}</span>
                        <span :class="[
                          'px-2 py-0.5 rounded text-xs font-medium',
                          order.orderType === 'ONCE' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                        ]">
                          {{ order.orderType === 'ONCE' ? '一次性服务' : `分期服务（${order.periodCount || 0}期）` }}
                        </span>
                      </div>
                      <span class="font-medium text-primary-600">¥{{ order.amount?.toLocaleString() || '0' }}</span>
                    </div>
                    <div class="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                      <div>
                        <span class="text-slate-500">产品：</span>
                        <span class="text-slate-700">{{ getProductName(order.productId) }}</span>
                      </div>
                      <div>
                        <span class="text-slate-500">执行人员：</span>
                        <span class="text-slate-700">{{ getExecutorName(order) }}</span>
                      </div>
                      <div>
                        <span class="text-slate-500">服务周期：</span>
                        <span class="text-slate-700">{{ order.serviceStart || '-' }} ~ {{ order.serviceEnd || '-' }}</span>
                      </div>
                      <div>
                        <span class="text-slate-500">账单数：</span>
                        <span class="text-slate-700">{{ order.billings?.length || 0 }} 笔</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="bg-slate-50 px-4 py-3 border-t border-slate-200 flex items-center justify-end gap-4">
                  <span class="text-sm text-slate-600">方案总金额</span>
                  <span class="text-2xl font-bold text-primary-600">¥{{ orderTotalAmount.toLocaleString() }}</span>
                </div>
              </div>

              <!-- 提示信息 -->
              <div class="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div class="flex items-start gap-2">
                  <svg class="w-5 h-5 text-amber-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
                  </svg>
                  <div class="text-sm text-amber-700">
                    <p class="font-medium">温馨提示</p>
                    <p class="mt-1">请确保所有信息准确无误后再提交。提交后将进入审批流程（如果需要）。</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 底部按钮 -->
        <div class="flex-shrink-0 px-6 py-4 border-t border-slate-200 flex items-center justify-between bg-slate-50">
          <button
            v-if="currentStep > 1"
            @click="prevStep"
            class="btn-secondary"
          >
            上一步
          </button>
          <div v-else></div>

          <div class="flex gap-3">
            <button @click="closeModal" class="btn-secondary">
              取消
            </button>
            <template v-if="currentStep < 4">
              <button v-if="!isEditingFormalPlan" @click="handleSaveDraft" :loading="saving" class="btn-secondary">
                暂存
              </button>
              <button
                @click="nextStep"
                class="btn-primary inline-flex items-center gap-2"
              >
                下一步
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                </svg>
              </button>
            </template>
            <template v-else>
              <button @click="handleSubmit" :loading="saving" class="btn-primary">
                提交方案
              </button>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { getCustomerPage } from '@/api/customer'
import RichTextEditor from '@/components/RichTextEditor.vue'
import ProductPicker from '@/components/ProductPicker.vue'
import { getProductPage, getProductCategories } from '@/api/product'
import { getUserList } from '@/api/user'
import { savePlan, updatePlan, getPlanDetail, togglePlanReminderStatus, toggleBillingReminderStatus, toggleOrderRenewalReminderStatus, deletePlanReminder, deleteBillingReminder, deleteOrderRenewalReminder } from '@/api/plan'
import { calculateServiceEndByMonthlyPeriods } from '@/utils/servicePeriod'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  isEdit: {
    type: Boolean,
    default: false
  },
  editData: {
    type: Object,
    default: () => null
  }
})

const emit = defineEmits(['update:visible', 'success'])

// 步骤相关
const currentStep = ref(1)
const saving = ref(false)
const customerLoading = ref(false)
const loadingOrders = ref(false)
const customerList = ref([])
const productList = ref([])
const categories = ref([])

const wizardSteps = [
  { label: '基本信息', value: 1 },
  { label: '订单与账单', value: 2 },
  { label: '提醒设置', value: 3 },
  { label: '确认信息', value: 4 }
]

// 表单数据
let localKeySeed = 0
const createLocalKey = (prefix) => `${prefix}-${Date.now()}-${localKeySeed++}`

const createBillingReminderSetting = () => ({
  enabled: false,
  advanceDays: 0,
  remindTime: '09:30:00',
  repeatEnabled: false,
  repeatIntervalValue: 1,
  repeatIntervalUnit: 'HOUR',
  maxReminderCount: 10
})

const createRenewalReminderSetting = () => ({
  enabled: false,
  advanceDays: 0,
  remindTime: '09:30:00',
  repeatEnabled: false,
  repeatIntervalValue: 1,
  repeatIntervalUnit: 'HOUR',
  maxReminderCount: 10
})

const normalizeRepeatInterval = (totalMinutes = 60) => {
  if (totalMinutes >= 24 * 60 && totalMinutes % (24 * 60) === 0) {
    return { repeatIntervalValue: totalMinutes / (24 * 60), repeatIntervalUnit: 'DAY' }
  }
  if (totalMinutes >= 60 && totalMinutes % 60 === 0) {
    return { repeatIntervalValue: totalMinutes / 60, repeatIntervalUnit: 'HOUR' }
  }
  return { repeatIntervalValue: totalMinutes, repeatIntervalUnit: 'MINUTE' }
}

const toRepeatIntervalMinutes = (value, unit) => {
  const intervalValue = value || 1
  if (unit === 'DAY') return intervalValue * 24 * 60
  if (unit === 'HOUR') return intervalValue * 60
  return intervalValue
}

const normalizeLocalDateTime = (value) => {
  if (!value || typeof value !== 'string') return value || null
  return value.includes(' ') ? value.replace(' ', 'T') : value
}

const planForm = reactive({
  id: null,
  customerId: '',
  name: '',
  nameTags: [],
  nameOptions: ['财务财税', '融资', '项目申报', '股权方案', '资产'],
  detailContent: '',
  remark: '',
  orders: [],
  reminders: [],
  billingReminderSetting: createBillingReminderSetting()
})

// 计算属性
const orderTotalAmount = computed(() => {
  return planForm.orders.reduce((sum, order) => sum + (order.amount || 0), 0)
})

// 是否编辑正式方案（不允许暂存草稿）
const isEditingFormalPlan = computed(() => {
  return props.isEdit && props.editData?.isDraft !== 1
})

// 辅助函数：获取客户名称
const getCustomerName = (customerId) => {
  const customer = customerList.value.find(c => c.id === customerId)
  return customer?.name || '-'
}

// 辅助函数：获取产品名称
const getProductName = (productId) => {
  const product = productList.value.find(p => p.id === productId)
  return product ? `${product.name} (${product.code})` : '-'
}

// 辅助函数：获取执行人员名称
const getExecutorName = (order) => {
  const executor = order.executorList?.find(u => u.id === order.executorId)
  return executor?.displayName || executor?.username || '-'
}

// 方案名称多选处理
watch(() => planForm.nameTags, (tags) => {
  planForm.name = tags.join('丨')
})

// 获取账单金额合计
const getBillingTotal = (order) => {
  if (!order.billings || order.billings.length === 0) return 0
  return order.billings.reduce((sum, b) => sum + (b.amount || 0), 0)
}

// 账单金额是否与订单金额一致
const isBillingTotalValid = (order) => {
  return Math.abs(getBillingTotal(order) - (order.amount || 0)) < 0.01
}

// 加载客户列表
const loadCustomers = async (keyword = '') => {
  customerLoading.value = true
  try {
    const data = await getCustomerPage({
      name: keyword || undefined,
      page: 1,
      size: 50
    })
    customerList.value = data.records || []
  } catch (error) {
    console.error('加载客户列表失败', error)
  } finally {
    customerLoading.value = false
  }
}

let customerSearchTimer = null
const handleCustomerSearch = (val) => {
  if (customerSearchTimer) clearTimeout(customerSearchTimer)
  customerSearchTimer = setTimeout(() => {
    loadCustomers(val)
  }, 300)
}

// 加载产品分类
const loadCategories = async () => {
  try {
    const data = await getProductCategories()
    categories.value = data || []
  } catch (error) {
    console.error('加载产品分类失败', error)
  }
}

// 加载产品列表
const loadProducts = async (keyword = '') => {
  try {
    const data = await getProductPage({ keyword: keyword || undefined, page: 1, size: 100 })
    productList.value = data.records || []
  } catch (error) {
    console.error('加载产品列表失败', error)
  }
}

// 产品选择变化
const handleProductChange = (order, product) => {
  order.productSearchKeyword = ''
  order.amount = 0
  order.executorId = null
  order.executorList = []
  if (product) {
    // Keep productList updated for confirmation-step name lookups
    if (!productList.value.some(p => p.id === product.id)) {
      productList.value.push(product)
    }
    order.productNameSnapshot = `${product.name} (${product.code})`
  }
  if (order.productId) {
    loadExecutors(order)
  }
}

// 加载执行人员列表
const createExecutorPagination = () => ({
  page: 1,
  size: 20,
  total: 0
})

const ensureExecutorState = (order) => {
  if (!order.executorList) order.executorList = []
  if (!order.executorPagination) order.executorPagination = createExecutorPagination()
  if (typeof order.executorLoading !== 'boolean') order.executorLoading = false
  if (typeof order.executorSearchKeyword !== 'string') order.executorSearchKeyword = ''
  if (typeof order.executorRequestId !== 'number') order.executorRequestId = 0
  if (!order.executorLoadedKeys) order.executorLoadedKeys = new Set()
  if (!order.executorLoadingKeys) order.executorLoadingKeys = new Set()
}

const loadExecutors = async (order, keyword = '', isLoadMore = false) => {
  ensureExecutorState(order)
  const normalizedKeyword = (keyword || '').trim()
  const nextPage = isLoadMore ? order.executorPagination.page + 1 : 1
  const requestKey = `${normalizedKeyword}::${nextPage}`
  if (order.executorLoadingKeys.has(requestKey) || order.executorLoadedKeys.has(requestKey)) return

  const requestId = order.executorRequestId + 1
  order.executorRequestId = requestId
  order.executorLoadingKeys.add(requestKey)
  order.executorLoading = true
  try {
    const data = await getUserList({
      username: normalizedKeyword || undefined,
      status: 0,
      page: nextPage,
      size: order.executorPagination.size
    })
    if (order.executorRequestId !== requestId) return
    const users = data?.records || []
    if (isLoadMore) {
      const existingIds = new Set(order.executorList.map(item => item.id))
      order.executorList = [
        ...order.executorList,
        ...users.filter(item => !existingIds.has(item.id))
      ]
      order.executorPagination.page = data?.current || order.executorPagination.page + 1
    } else {
      order.executorList = users
      order.executorPagination.page = data?.current || 1
    }
    order.executorPagination.total = data?.total || 0
    order.executorLoadedKeys.add(requestKey)
  } catch (error) {
    console.error('加载执行人员列表失败', error)
  } finally {
    order.executorLoadingKeys.delete(requestKey)
    if (order.executorRequestId === requestId) {
      order.executorLoading = false
    }
  }
}

// 执行人员搜索
const handleExecutorSearch = (val, order) => {
  ensureExecutorState(order)
  order.executorSearchKeyword = val
  if (order.executorSearchTimer) {
    clearTimeout(order.executorSearchTimer)
  }
  if (!val && order.executorList.length > 0) return
  order.executorSearchTimer = setTimeout(() => {
    loadExecutors(order, val, false)
  }, 300)
}

const handleExecutorDropdownVisible = (visible, order) => {
  ensureExecutorState(order)
  if (visible && order.executorList.length === 0) {
    loadExecutors(order, '', false)
  }
}

const loadMoreExecutors = (order) => {
  ensureExecutorState(order)
  loadExecutors(order, order.executorSearchKeyword, true)
}

// 向导步骤控制
const nextStep = () => {
  if (currentStep.value === 1) {
    if (!planForm.name.trim()) {
      ElMessage.warning('请输入方案名称')
      return
    }
    if (!planForm.customerId) {
      ElMessage.warning('请选择客户')
      return
    }
  }
  if (currentStep.value < 4) {
    currentStep.value++
  }
}

const prevStep = () => {
  if (currentStep.value > 1) {
    currentStep.value--
  }
}

// 订单操作
const addOrder = () => {
  const newOrder = {
    _localKey: createLocalKey('order'),
    id: null,
    productId: '',
    productSearchKeyword: '',
    orderType: 'ONCE',
    periodCount: null,
    serviceStart: '',
    serviceEnd: '',
    amount: 0,
    remark: '',
    executorId: null,
    executorList: [],
    executorLoading: false,
    executorSearchKeyword: '',
    executorRequestId: 0,
    executorSearchTimer: null,
    executorLoadedKeys: new Set(),
    executorLoadingKeys: new Set(),
    executorPagination: createExecutorPagination(),
    billings: [],
    renewalReminderSetting: createRenewalReminderSetting()
  }
  planForm.orders.push(newOrder)
}

const removeOrder = (index) => {
  planForm.orders.splice(index, 1)
}

const handleOrderTypeChange = (order) => {
  if (order.orderType === 'ONCE') {
    order.periodCount = null
  } else {
    order.periodCount = calculateNaturalMonthPeriods(order.serviceStart, order.serviceEnd) || order.periodCount || 1
  }
  if (!order.renewalReminderSetting) {
    order.renewalReminderSetting = createRenewalReminderSetting()
  }
}

const calculateNaturalMonthPeriods = (startDate, endDate) => {
  if (!startDate || !endDate) return null
  const start = new Date(startDate)
  const end = new Date(endDate)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return null
  return (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()) + 1
}

const updatePeriodCountByServiceRange = (order) => {
  if (order.orderType !== 'PERIODIC') return
  const periodCount = calculateNaturalMonthPeriods(order.serviceStart, order.serviceEnd)
  if (periodCount) {
    order.periodCount = periodCount
  }
}

const updateServiceEndByPeriodCount = (order) => {
  if (order.orderType !== 'PERIODIC') return
  const serviceEnd = calculateServiceEndByMonthlyPeriods(order.serviceStart, order.periodCount)
  if (serviceEnd) {
    order.serviceEnd = serviceEnd
  }
}

// 账单操作
const generateBillings = (order) => {
  if (order.amount === null || order.amount === undefined || order.amount === '') {
    ElMessage.warning('请先输入订单金额')
    return
  }

  const periodCount = order.orderType === 'PERIODIC' ? (order.periodCount || 1) : 1
  const amountPerPeriod = order.amount / periodCount

  const startDate = order.serviceStart ? new Date(order.serviceStart) : new Date()
  const bills = []

  for (let i = 1; i <= periodCount; i++) {
    const dueDate = new Date(startDate)
    dueDate.setMonth(dueDate.getMonth() + (i - 1))
    const dueDateStr = dueDate.toISOString().split('T')[0]

    bills.push({
      periodIndex: i,
      amount: Number(amountPerPeriod.toFixed(2)),
      dueDate: dueDateStr
    })
  }

  order.billings = bills
}

const addBilling = (order) => {
  if (!order.billings) order.billings = []
  const maxPeriod = order.billings.reduce((max, b) => Math.max(max, b.periodIndex || 0), 0)
  order.billings.push({
    periodIndex: maxPeriod + 1,
    amount: 0,
    dueDate: ''
  })
}

// 账单金额变化时同步订单金额
const syncOrderAmountFromBillings = (order) => {
  if (order.billings && order.billings.length > 0) {
    order.amount = getBillingTotal(order)
  }
}

const removeBilling = (order, index) => {
  order.billings.splice(index, 1)
  syncOrderAmountFromBillings(order)
}

// 提醒操作
const addReminder = () => {
  planForm.reminders.push({
    _localKey: createLocalKey('reminder'),
    enabled: true,
    firstReminderTime: '',
    reminderItem: '',
    repeatEnabled: false,
    repeatIntervalValue: 1,
    repeatIntervalUnit: 'HOUR',
    maxReminderCount: 10
  })
}

const toggleReminder = async (reminder) => {
  // 计算新的开关状态：当前为false时切换为true，否则切换为false
  const newEnabled = reminder.enabled === false ? true : false
  // 如果是已保存的提醒（有待切换ID），调用API
  if (reminder.id) {
    try {
      await togglePlanReminderStatus(reminder.id, newEnabled)
      reminder.enabled = newEnabled
    } catch (error) {
      console.error('切换提醒状态失败', error)
    }
  } else {
    // 新增的提醒，直接切换本地状态
    reminder.enabled = newEnabled
  }
}

// 切换方案账单提醒
const toggleBillingReminder = async () => {
  const setting = planForm.billingReminderSetting
  const newEnabled = !setting.enabled
  // 如果是已保存的账单提醒（有ID），调用API
  if (setting.id) {
    try {
      await toggleBillingReminderStatus(planForm.id, newEnabled)
      setting.enabled = newEnabled
    } catch (error) {
      console.error('切换账单提醒状态失败', error)
    }
  } else {
    // 新增的账单提醒，直接切换本地状态
    setting.enabled = newEnabled
  }
}

// 切换订单续约提醒
const toggleOrderRenewalReminder = async (order) => {
  if (!order.renewalReminderSetting) {
    order.renewalReminderSetting = createRenewalReminderSetting()
  }
  const newEnabled = !order.renewalReminderSetting.enabled
  // 如果是已保存的续约提醒（有ID），调用API
  if (order.renewalReminderSetting.id) {
    try {
      await toggleOrderRenewalReminderStatus(order.id, newEnabled)
      order.renewalReminderSetting.enabled = newEnabled
    } catch (error) {
      console.error('切换续约提醒状态失败', error)
    }
  } else {
    // 新增的续约提醒，直接切换本地状态
    order.renewalReminderSetting.enabled = newEnabled
  }
}

// 删除方案跟进提醒
const removeReminder = async (index) => {
  const reminder = planForm.reminders[index]
  if (!reminder) {
    planForm.reminders.splice(index, 1)
    return
  }
  if (reminder.id) {
    try {
      await deletePlanReminder(reminder.id)
      planForm.reminders.splice(index, 1)
    } catch (error) {
      console.error('删除提醒失败', error)
    }
  } else {
    planForm.reminders.splice(index, 1)
  }
}

// 删除方案账单提醒
const removeBillingReminder = async () => {
  const setting = planForm.billingReminderSetting
  if (setting.id) {
    try {
      await deleteBillingReminder(planForm.id)
      planForm.billingReminderSetting = createBillingReminderSetting()
    } catch (error) {
      console.error('删除账单提醒失败', error)
    }
  } else {
    planForm.billingReminderSetting = createBillingReminderSetting()
  }
}

// 删除订单续约提醒
const removeOrderRenewalReminder = async (order) => {
  if (!order.renewalReminderSetting) return
  if (order.renewalReminderSetting.id) {
    try {
      await deleteOrderRenewalReminder(order.id)
      order.renewalReminderSetting = createRenewalReminderSetting()
    } catch (error) {
      console.error('删除续约提醒失败', error)
    }
  } else {
    order.renewalReminderSetting = createRenewalReminderSetting()
  }
}

// 添加订单续约提醒
const addOrderRenewalReminder = (order) => {
  order.renewalReminderSetting = {
    ...createRenewalReminderSetting(),
    _added: true
  }
}

// 添加账单提醒
const addBillingReminder = () => {
  planForm.billingReminderSetting = {
    ...createBillingReminderSetting(),
    _added: true
  }
}

// 表单验证（只验证基本信息，不验证订单）
const validateForm = () => {
  if (!planForm.name.trim()) {
    ElMessage.warning('请输入方案名称')
    return false
  }
  if (!planForm.customerId) {
    ElMessage.warning('请选择客户')
    return false
  }
  return true
}

const validateReminderSettings = () => {
  for (let i = 0; i < planForm.reminders.length; i++) {
    const reminder = planForm.reminders[i]
    if (reminder.enabled === false) continue

    if (!reminder.firstReminderTime) {
      ElMessage.warning(`请完善提醒${i + 1}的首次提醒时间`)
      return false
    }
    if (!String(reminder.reminderItem || '').trim()) {
      ElMessage.warning(`请完善提醒${i + 1}的提醒事项`)
      return false
    }
    if (reminder.repeatEnabled) {
      if (!Number.isFinite(reminder.repeatIntervalValue) || reminder.repeatIntervalValue < 1) {
        ElMessage.warning(`请设置提醒${i + 1}的连续提醒间隔`)
        return false
      }
      if (!Number.isFinite(reminder.maxReminderCount) || reminder.maxReminderCount < 1) {
        ElMessage.warning(`请设置提醒${i + 1}的最大提醒次数`)
        return false
      }
    }
  }

  const billingReminder = planForm.billingReminderSetting
  if (billingReminder?.enabled) {
    if (!Number.isFinite(billingReminder.advanceDays) || billingReminder.advanceDays < 0) {
      ElMessage.warning('请设置正确的账单提醒提前天数')
      return false
    }
    if (!billingReminder.remindTime) {
      ElMessage.warning('请设置账单提醒时间')
      return false
    }
    if (billingReminder.repeatEnabled) {
      if (!Number.isFinite(billingReminder.repeatIntervalValue) || billingReminder.repeatIntervalValue < 1) {
        ElMessage.warning('请设置正确的账单连续提醒间隔')
        return false
      }
      if (!Number.isFinite(billingReminder.maxReminderCount) || billingReminder.maxReminderCount < 1) {
        ElMessage.warning('请设置正确的账单最大提醒次数')
        return false
      }
    }
  }

  for (let i = 0; i < planForm.orders.length; i++) {
    const order = planForm.orders[i]
    if (!order.renewalReminderSetting?.enabled) continue

    if (!Number.isFinite(order.renewalReminderSetting.advanceDays) || order.renewalReminderSetting.advanceDays < 0) {
      ElMessage.warning(`请设置订单${i + 1}正确的续约提醒提前天数`)
      return false
    }
    if (!order.renewalReminderSetting.remindTime) {
      ElMessage.warning(`请设置订单${i + 1}的续约提醒时间`)
      return false
    }
    if (order.renewalReminderSetting.repeatEnabled) {
      if (!Number.isFinite(order.renewalReminderSetting.repeatIntervalValue) || order.renewalReminderSetting.repeatIntervalValue < 1) {
        ElMessage.warning(`请设置订单${i + 1}正确的续约连续提醒间隔`)
        return false
      }
      if (!Number.isFinite(order.renewalReminderSetting.maxReminderCount) || order.renewalReminderSetting.maxReminderCount < 1) {
        ElMessage.warning(`请设置订单${i + 1}正确的续约最大提醒次数`)
        return false
      }
    }
  }

  return true
}

// 检查是否存在金额为0的订单或账单
const hasZeroAmountOrdersOrBillings = () => {
  for (const order of planForm.orders) {
    if (order.amount === 0) {
      return true
    }
    if (order.billings?.some(b => b.amount === 0)) {
      return true
    }
  }
  return false
}

// 构建提交数据
const buildPlanCommand = (isDraft) => {
  return {
    plan: {
      id: planForm.id || null,
      customerId: planForm.customerId,
      name: planForm.name,
      isDraft: isDraft,
      totalAmount: orderTotalAmount.value,
      payableAmount: orderTotalAmount.value,
      remark: planForm.remark,
      detailContent: planForm.detailContent
    },
    orders: planForm.orders.map(o => ({
      id: o.id || null,
      productId: o.productId,
      orderType: o.orderType,
      periodCount: o.orderType === 'PERIODIC' ? o.periodCount : undefined,
      serviceStart: o.serviceStart || undefined,
      serviceEnd: o.serviceEnd || undefined,
      amount: o.amount,
      remark: o.remark || undefined,
      executorId: o.executorId || undefined,
      billings: o.billings || [],
      renewalReminderSetting: o.renewalReminderSetting?.enabled ? {
        id: o.renewalReminderSetting.id || null,
        enabled: true,
        advanceDays: o.renewalReminderSetting.advanceDays || 0,
        remindTime: o.renewalReminderSetting.remindTime || '09:30:00',
        repeatEnabled: o.renewalReminderSetting.repeatEnabled || false,
        repeatIntervalMinutes: o.renewalReminderSetting.repeatEnabled
          ? toRepeatIntervalMinutes(o.renewalReminderSetting.repeatIntervalValue, o.renewalReminderSetting.repeatIntervalUnit)
          : null,
        maxReminderCount: o.renewalReminderSetting.repeatEnabled ? (o.renewalReminderSetting.maxReminderCount || 10) : null
      } : null
    })),
    reminders: planForm.reminders.map(r => {
      const repeatIntervalMinutes = r.repeatEnabled
        ? toRepeatIntervalMinutes(r.repeatIntervalValue, r.repeatIntervalUnit)
        : null
      return {
        id: r.id || null,
        firstReminderTime: normalizeLocalDateTime(r.firstReminderTime),
        reminderItem: r.reminderItem || '',
        repeatEnabled: r.repeatEnabled || false,
        repeatIntervalMinutes,
        maxReminderCount: r.repeatEnabled ? (r.maxReminderCount || 10) : null,
        enabled: r.enabled !== false
      }
    }),
    billingReminderSetting: planForm.billingReminderSetting?.enabled ? {
      id: planForm.billingReminderSetting.id || null,
      enabled: true,
      advanceDays: planForm.billingReminderSetting.advanceDays || 0,
      remindTime: planForm.billingReminderSetting.remindTime || '09:30:00',
      repeatEnabled: planForm.billingReminderSetting.repeatEnabled || false,
      repeatIntervalMinutes: planForm.billingReminderSetting.repeatEnabled
        ? toRepeatIntervalMinutes(planForm.billingReminderSetting.repeatIntervalValue, planForm.billingReminderSetting.repeatIntervalUnit)
        : null,
      maxReminderCount: planForm.billingReminderSetting.repeatEnabled ? (planForm.billingReminderSetting.maxReminderCount || 10) : null
    } : null,
    asDraft: isDraft === 1
  }
}

// 保存和提交操作
const handleSaveDraft = async () => {
  if (!validateForm()) return
  if (!validateReminderSettings()) return

  // 检查是否存在金额为0的订单或账单
  if (hasZeroAmountOrdersOrBillings()) {
    try {
      await ElMessageBox.confirm('订单或账单中存在金额为0的条目，确认要保存吗？', '金额确认', {
        confirmButtonText: '确认保存',
        cancelButtonText: '取消',
        type: 'warning'
      })
    } catch {
      return // 用户取消
    }
  }

  saving.value = true
  try {
    const command = buildPlanCommand(1)
    if (props.isEdit) {
      await updatePlan(command)
    } else {
      await savePlan(command)
    }
    ElMessage.success('保存成功')
    closeModal()
    emit('success')
  } catch (error) {
    ElMessage.error(error.message || '保存失败')
  } finally {
    saving.value = false
  }
}

const handleSubmit = async () => {
  if (!validateForm()) return
  if (!validateReminderSettings()) return

  // 检查是否存在金额为0的订单或账单
  if (hasZeroAmountOrdersOrBillings()) {
    try {
      await ElMessageBox.confirm('订单或账单中存在金额为0的条目，确认要提交吗？', '金额确认', {
        confirmButtonText: '确认提交',
        cancelButtonText: '取消',
        type: 'warning'
      })
    } catch {
      return // 用户取消
    }
  }

  saving.value = true
  try {
    const command = buildPlanCommand(0)
    const result = await updatePlan(command)
    if (result?.needApproval) {
      ElMessage.success('提交成功，方案已进入审批流程')
    } else {
      ElMessage.success('提交成功')
    }
    closeModal()
    emit('success')
  } catch (error) {
    ElMessage.error(error.message || '提交失败')
  } finally {
    saving.value = false
  }
}

// 关闭弹窗
const closeModal = () => {
  emit('update:visible', false)
}

// 初始化表单数据
const initForm = async () => {
  currentStep.value = 1
  if (props.isEdit && props.editData) {
    // 编辑模式：回填方案基本信息
    const nameTags = props.editData.name ? props.editData.name.split('丨') : []
    Object.assign(planForm, {
      id: props.editData.id,
      customerId: props.editData.customerId,
      name: props.editData.name,
      nameTags: nameTags,
      nameOptions: ['财务财税', '融资', '项目申报', '股权方案', '资产'],
      detailContent: props.editData.detailContent || '',
      remark: props.editData.remark || '',
      orders: [],
      reminders: [],
      billingReminderSetting: createBillingReminderSetting()
    })

    // 加载方案详情以获取订单数据
    loadingOrders.value = true
    try {
      const detail = await getPlanDetail(props.editData.id)
      // 回填方案详情内容
      planForm.detailContent = detail.detailContent || ''
      if (detail?.orders?.length) {
        planForm.orders = detail.orders.map(order => ({
          _localKey: createLocalKey('order'),
          id: order.id,
          productId: order.productId,
          productSearchKeyword: '',
          orderType: order.orderType,
          periodCount: order.periodCount,
          serviceStart: order.serviceStart || '',
          serviceEnd: order.serviceEnd || '',
          amount: order.amount || 0,
          remark: order.remark || '',
          executorId: order.executorId,
          executorList: [],
          executorLoading: false,
          executorSearchKeyword: '',
          executorRequestId: 0,
          executorSearchTimer: null,
          executorLoadedKeys: new Set(),
          executorLoadingKeys: new Set(),
          executorPagination: createExecutorPagination(),
          renewalReminderSetting: createRenewalReminderSetting(),
          billings: (order.billings || []).map(b => ({
            id: b.id,
            periodIndex: b.periodIndex,
            amount: b.amount || 0,
            dueDate: b.dueDate || ''
          }))
        }))

        // 为每个订单加载执行人列表
        for (const order of planForm.orders) {
          await loadExecutors(order)
        }
      }

      // 回填提醒数据
      if (detail?.reminders?.length) {
        planForm.reminders = detail.reminders.map(r => {
          // 将分钟转换为值和单位
          const { repeatIntervalValue, repeatIntervalUnit } = normalizeRepeatInterval(r.repeatIntervalMinutes || 60)
          return {
            _localKey: createLocalKey('reminder'),
            id: r.id,
            firstReminderTime: normalizeLocalDateTime(r.firstReminderTime) || '',
            reminderItem: r.reminderItem || '',
            repeatEnabled: r.repeatEnabled || false,
            repeatIntervalValue,
            repeatIntervalUnit,
            maxReminderCount: r.maxReminderCount || 10,
            enabled: r.enabled
          }
        })
      }

      // 回填方案账单提醒设置
      if (detail?.billingReminderSetting) {
        const { repeatIntervalValue, repeatIntervalUnit } = normalizeRepeatInterval(detail.billingReminderSetting.repeatIntervalMinutes || 60)
        planForm.billingReminderSetting = {
          ...createBillingReminderSetting(),
          id: detail.billingReminderSetting.id,
          enabled: detail.billingReminderSetting.enabled,
          advanceDays: detail.billingReminderSetting.advanceDays || 0,
          remindTime: detail.billingReminderSetting.remindTime || '09:30:00',
          repeatEnabled: detail.billingReminderSetting.repeatEnabled || false,
          repeatIntervalValue,
          repeatIntervalUnit,
          maxReminderCount: detail.billingReminderSetting.maxReminderCount || 10
        }
      }

      // 回填订单续约提醒设置
      if (detail?.orders?.length) {
        detail.orders.forEach((orderDetail, index) => {
          if (planForm.orders[index] && orderDetail.renewalReminderSetting) {
            const { repeatIntervalValue, repeatIntervalUnit } = normalizeRepeatInterval(orderDetail.renewalReminderSetting.repeatIntervalMinutes || 60)
            planForm.orders[index].renewalReminderSetting = {
              ...createRenewalReminderSetting(),
              id: orderDetail.renewalReminderSetting.id,
              enabled: orderDetail.renewalReminderSetting.enabled,
              advanceDays: orderDetail.renewalReminderSetting.advanceDays || 0,
              remindTime: orderDetail.renewalReminderSetting.remindTime || '09:30:00',
              repeatEnabled: orderDetail.renewalReminderSetting.repeatEnabled || false,
              repeatIntervalValue,
              repeatIntervalUnit,
              maxReminderCount: orderDetail.renewalReminderSetting.maxReminderCount || 10
            }
          }
        })
      }
    } catch (error) {
      console.error('加载方案详情失败', error)
    } finally {
      loadingOrders.value = false
    }
  } else {
    // 新建模式下，如果传入了 editData 中的客户信息，则默认选中该客户
    const defaultCustomerId = props.editData?.customerId || ''
    const defaultCustomerName = props.editData?.customerName || ''

    Object.assign(planForm, {
      id: null,
      customerId: defaultCustomerId,
      name: '',
      nameTags: [],
      nameOptions: ['财务财税', '融资', '项目申报', '股权方案', '资产'],
      detailContent: '',
      remark: '',
      orders: [],
      reminders: [],
      billingReminderSetting: createBillingReminderSetting()
    })

    // 如果有默认客户，将其添加到客户列表中以便显示
    if (defaultCustomerId && defaultCustomerName) {
      customerList.value = [{ id: defaultCustomerId, name: defaultCustomerName }]
    }
  }
}

// 监听 visible 变化
watch(() => props.visible, async (val) => {
  if (val) {
    await initForm()
    loadCustomers('')
    loadProducts()
    loadCategories()
  }
}, { immediate: true })

loadCustomers('')
loadProducts()
loadCategories()
</script>
